import React from 'react';
import { ShoppingBag, Package, Truck, Store, Calendar, Download, ChevronDown } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { MetricCard } from './components/MetricCard';
import { SalesOverviewChart } from './components/SalesOverviewChart';
import { SalesByCityChart } from './components/SalesByCityChart';
import { SalesDistribution } from './components/SalesDistribution';

// Sparkline Mock Data for KPI cards
const totalSaleSparkline = [
  { value: 40 }, { value: 60 }, { value: 50 }, { value: 80 }, { value: 70 }, { value: 90 }, { value: 110 }
];
const totalProductsSparkline = [
  { value: 30 }, { value: 40 }, { value: 35 }, { value: 60 }, { value: 55 }, { value: 75 }, { value: 90 }
];
const totalGTSparkline = [
  { value: 20 }, { value: 35 }, { value: 30 }, { value: 50 }, { value: 65 }, { value: 60 }, { value: 85 }
];
const totalMTSparkline = [
  { value: 25 }, { value: 30 }, { value: 45 }, { value: 40 }, { value: 55 }, { value: 70 }, { value: 80 }
];

export const Dashboard: React.FC = () => {
  const profile = useAppSelector((state) => state.auth.profile);
  const rawName = profile?.user?.name || profile?.name;
  const userName = rawName || 'System Admin';

  const dateRange = '21 Aug 2026 - 21 Aug 2026';

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Welcome & Actions Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
            Welcome back, {userName} <span className="inline-block animate-bounce">👋</span>
          </h1>
          <p className="text-xs font-semibold text-slate-400 mt-1">
            Here's what's happening with your business today.
          </p>
        </div>

        {/* Right Toolbar Actions */}
        <div className="flex items-center gap-3">
          {/* Date Picker Button */}
          <div className="relative group">
            <button className="px-3.5 py-2 bg-white border border-[#ECECF2] hover:border-slate-300 rounded-xl text-xs font-bold text-slate-700 shadow-sm flex items-center gap-2 transition-all">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span>{dateRange}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 ml-1" />
            </button>
          </div>

          {/* Export Report Button */}
          <button className="px-4 py-2 bg-[#C1272D] hover:bg-[#A81E23] text-white rounded-xl text-xs font-bold shadow-md shadow-[#C1272D]/20 flex items-center gap-2 cursor-pointer transition-all active:scale-95">
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Top 4 KPI Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <MetricCard
          title="Total Sale"
          value="₹ 12,45,678"
          changeText="+12.5% from yesterday"
          changeColor="text-emerald-600"
          icon={ShoppingBag}
          iconBgColor="bg-rose-100/70"
          iconColor="text-rose-600"
          sparklineData={totalSaleSparkline}
          sparklineColor="#C1272D"
        />

        <MetricCard
          title="Total Products"
          value="256"
          changeText="+8 new products"
          changeColor="text-emerald-600"
          icon={Package}
          iconBgColor="bg-blue-100/70"
          iconColor="text-blue-600"
          sparklineData={totalProductsSparkline}
          sparklineColor="#2563EB"
        />

        <MetricCard
          title="Total GT"
          value="1,245"
          changeText="+18 this month"
          changeColor="text-emerald-600"
          icon={Truck}
          iconBgColor="bg-emerald-100/70"
          iconColor="text-emerald-600"
          sparklineData={totalGTSparkline}
          sparklineColor="#16A34A"
        />

        <MetricCard
          title="Total MT"
          value="589"
          changeText="+10 this month"
          changeColor="text-emerald-600"
          icon={Store}
          iconBgColor="bg-orange-100/70"
          iconColor="text-orange-600"
          sparklineData={totalMTSparkline}
          sparklineColor="#EA580C"
        />
      </div>

      {/* Middle Row Charts Grid (Sales Overview & Sales by City) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <SalesOverviewChart />
        </div>
        <div className="lg:col-span-5">
          <SalesByCityChart />
        </div>
      </div>

      {/* Bottom Row Grid (Sales Distribution) */}
      <div className="grid grid-cols-1 gap-6">
        <div>
          <SalesDistribution />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
